import React, { FC, PropsWithChildren, ReactElement, ReactNode } from 'react';

import './ItvRowCollection.scss';
import { layoutRegistry } from './layouts/layoutRegistry';
import { LayoutRenderer, RowCollectionContentType, RowCollectionLayout } from './layouts/types';

type ItvRowCollectionProps = PropsWithChildren<{
  type: RowCollectionContentType;
  layout_type: RowCollectionLayout;
  title: string;
  className?: string;
  id?: string;
  showViewAll?: boolean;
  onViewAll?: () => void;
  viewAllLabel?: string;
  headerAction?: ReactNode;
}>;

const CONTENT_TYPE_LABEL: Record<RowCollectionContentType, string> = {
  games: 'Games',
  bingo: 'Bingo',
  promotions: 'Promotions',
  'bingo+games': 'Bingo & Games',
};

const LayoutFallback: LayoutRenderer = ({ layoutType, placeholder }) => (
  <div className="itv-row-collection__fallback">
    <p className="itv-row-collection__fallback-title">Layout "{layoutType}" is not available yet.</p>
    {placeholder}
  </div>
);

const getPlaceholder = (type: RowCollectionContentType, layoutType: RowCollectionLayout): ReactElement => (
  <div className="itv-row-collection__placeholder">
    <p className="itv-row-collection__placeholder-title">{CONTENT_TYPE_LABEL[type]} content placeholder</p>
    <p className="itv-row-collection__placeholder-copy">
      Pass children to ItvRowCollection to populate the "{layoutType}" layout.
    </p>
  </div>
);

export const ItvRowCollection: FC<ItvRowCollectionProps> = ({
  type,
  layout_type,
  title,
  className,
  id,
  children,
  showViewAll = true,
  onViewAll,
  viewAllLabel = 'View all',
  headerAction,
}) => {
  const LayoutComponent = layoutRegistry[layout_type] ?? LayoutFallback;
  const placeholder = getPlaceholder(type, layout_type);
  const sectionClassName = [
    'itv-row-collection',
    `itv-row-collection--${layout_type}`,
    `itv-row-collection--content-${type}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName} id={id} data-layout={layout_type}>
      <header className="itv-row-collection__header">
        <div className="itv-row-collection__header-text">
          <h2 className="itv-row-collection__title">{title}</h2>
        </div>
        {headerAction && <div className="itv-row-collection__header-action">{headerAction}</div>}
      </header>

      <LayoutComponent layoutType={layout_type} placeholder={placeholder} contentType={type}>
        {children}
      </LayoutComponent>
      {showViewAll && (
        <div className="itv-row-collection__view-all-wrapper">
          <button
            type="button"
            className="itv-row-collection__view-all"
            onClick={onViewAll}
            aria-label={`${viewAllLabel} ${title}`}
          >
            {viewAllLabel}
          </button>
        </div>
      )}
    </section>
  );
};
